import { useTheme } from '@mui/material/styles';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Card, CardContent } from 'src/components/Card';
import { Skeleton } from 'src/components/Skeleton';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';

import { Button } from './Button';
import { Divider } from './Divider';
import { SectionHeader } from './SectionHeader';

interface StickyCardProps {
  actions?: React.ReactNode;
  children: React.ReactNode;
  contentSx?;
  /** When set, clicking the header (outside other controls) calls this. */
  onHeaderClick?: () => void;
  stickyOffset?: number;
  subtitle?: React.ReactNode;
  sx?;
  title?: React.ReactNode;
}

export const StickyCard: React.FC<StickyCardProps> = ({
  actions,
  children,
  contentSx,
  onHeaderClick,
  stickyOffset = 56,
  subtitle,
  sx,
  title,
}) => {
  const theme = useTheme();
  const { inset } = useNativeBridge();
  const styles = { overflow: 'clip', position: 'relative' };

  const handleHeaderClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!onHeaderClick) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }
    if (target.closest('button, a[href], input, select, textarea')) {
      return;
    }
    onHeaderClick();
  };

  return (
    <Card sx={[styles, ...(Array.isArray(sx) ? sx : [sx])]}>
      {title && (
        <Box
          onClick={onHeaderClick ? handleHeaderClick : undefined}
          sx={{
            position: 'sticky',
            top: `calc(${stickyOffset}px + ${inset.top}px)`,
            left: '0px',
            zIndex: '10',
            backdropFilter: 'blur(10px)',
            backgroundColor: `rgba(${theme.vars.palette.background.defaultChannel} / 0.6)`,
            ...(onHeaderClick ? { cursor: 'pointer' } : {}),

            ...theme.applyStyles('dark', {
              backgroundColor: `rgba(${theme.vars.palette.neutral['900Channel']} / 0.6)`,
            }),
          }}
        >
          <SectionHeader title={title} subtitle={subtitle} actions={actions} />
          <Divider sx={{ opacity: 0.7 }} />
        </Box>
      )}
      <CardContent sx={{ '&&': { px: 2, py: 3, ...contentSx } }}>
        {children}
      </CardContent>
    </Card>
  );
};

interface StickyCardSkeletonProps {
  title?: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  noActions?: boolean;
  sx?;
}

export const StickyCardSkeleton: React.FC<StickyCardSkeletonProps> = ({
  title,
  children,
  actions,
  noActions = true,
  sx,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'stickyCard' });
  const skeletonTitle = title || (
    <Skeleton variant="text" width={250}></Skeleton>
  );

  const skeletonActions =
    !noActions &&
    (actions || (
      <Skeleton variant="rectangular" sx={{ borderRadius: 1 }}>
        <Button size="small">{t('sampleWidth')}</Button>
      </Skeleton>
    ));

  const skeletonChildren = children || (
    <>
      <Skeleton variant="text" width="100%"></Skeleton>
      <Skeleton variant="text" width="100%"></Skeleton>
      <Skeleton variant="text" width="100%"></Skeleton>
    </>
  );

  return (
    <StickyCard actions={skeletonActions} sx={sx} title={skeletonTitle}>
      {skeletonChildren}
    </StickyCard>
  );
};
